'use client';
import * as React from 'react';
import { forwardRef, useMemo } from 'react';
import className from 'clsx';
import { MtsDsSpinner } from '@mts-ds/granat-react-spinner';
import styles from './button.module.scss.js';

/**
 * Компонент Button
 */
const Button = /*#__PURE__*/forwardRef(({
  children,
  color = 'primary',
  disabled,
  format = 'button',
  loading,
  size = 'm',
  type,
  fluid,
  onFocus,
  onClick,
  onBlur,
  iconRight,
  role,
  ariaLabel,
  ariaDescription,
  ariaHidden = false,
  tabIndex
}, ref) => {
  const onlyText = useMemo(() => {
    const arrayChildren = React.Children.toArray(children);
    return arrayChildren.length === 1 && (
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    arrayChildren[0]?.props?.slot !== 'icon' || typeof arrayChildren[0] === 'string');
  }, [children]);
  const spinnerColor = useMemo(() => {
    switch (color) {
      case 'primary':
        return 'white';
      case 'alternative':
        return 'inverted';
      case 'negative':
      case 'negative-inverted':
        return 'negative';
      default:
        return 'black';
    }
  }, [color]);
  return /*#__PURE__*/React.createElement("button", {
    onClick: onClick,
    tabIndex: tabIndex ?? (loading || disabled ? -1 : 0),
    onFocus: onFocus,
    onBlur: onBlur
    /* eslint-disable-next-line react/button-has-type */,
    type: type,
    disabled: disabled || loading,
    className: className(styles['mtsds-button'], styles[`mtsds-button--color-${color}`], styles[`mtsds-button--size-${size}`], styles[`mtsds-button--format-${format}`], disabled && styles['mtsds-button--disabled'], loading && styles['mtsds-button--loading'], fluid && styles['mtsds-button--fluid'], iconRight && styles['mtsds-button--icon-right']),
    ref: ref,
    role: role,
    "aria-label": ariaLabel
    /* eslint-disable-next-line jsx-a11y/aria-props */,
    "aria-description": ariaDescription,
    "aria-hidden": ariaHidden,
    "aria-live": loading ? 'polite' : 'off',
    "aria-busy": loading
  }, !!loading && /*#__PURE__*/React.createElement("div", {
    className: styles['mtsds-button__spinner'],
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(MtsDsSpinner, {
    size: size === 's' ? 's' : 'm',
    color: spinnerColor
  })), format === 'button' && /*#__PURE__*/React.createElement("div", {
    className: className(onlyText ? styles['mtsds-button__text-container'] : styles['mtsds-button__text-icon-container']),
    role: "presentation"
  }, children), format !== 'button' && /*#__PURE__*/React.createElement("div", {
    className: styles['mtsds-button__icon-container'],
    "aria-hidden": true
  }, children));
});

export { Button, Button as MtsDsButton };
